/**
 * Aviso flutuante — a confirmação que não interrompe.
 *
 * Pedido do Lucas (04/08): *"esse aviso que aparece no topo da tela toda vez
 * que algo é feito ou cadastrado e precisa apertar ok, diminui a
 * produtividade"*. Ele tem razão, e a razão é específica: quem concilia 130
 * itens ou cadastra material em série faz a mesma ação dezenas de vezes
 * seguidas, e cada `alert()` insere um clique que não decide nada — só
 * atrapalha o ritmo.
 *
 * A REGRA, dita por ele e respeitada aqui:
 *
 *   DEU CERTO   → aviso flutuante, some sozinho. A pessoa vê pelo canto do
 *                 olho e continua; se perder, não perdeu nada, porque a tela
 *                 já mostra o resultado.
 *
 *   DEU ERRADO  → continua exigindo OK. Erro que some sozinho é erro que
 *   ou ATENÇÃO    ninguém leu, e aqui isso significa alguém seguir adiante
 *                 achando que gravou.
 *
 * `confirm()` também fica onde está: perguntar antes de apagar não é aviso, é
 * decisão.
 */

type Tom = 'ok' | 'atencao';

let pilha: HTMLElement | null = null;

function area(): HTMLElement {
  if (pilha?.isConnected) return pilha;
  pilha = document.createElement('div');
  pilha.className = 'avisos';
  // `aria-live="polite"` faz o leitor de tela anunciar sem interromper o que
  // a pessoa está fazendo — o mesmo contrato do aviso visual.
  pilha.setAttribute('aria-live', 'polite');
  document.body.appendChild(pilha);
  return pilha;
}

/**
 * Mostra e some. Sem botão, sem foco roubado.
 *
 * A duração acompanha o tamanho do texto: quem tem mais para ler tem mais
 * tempo. Piso de 2,5 s (senão some antes de o olho chegar) e teto de 6 s
 * (acima disso vira poluição, não informação).
 */
export function avisar(mensagem: string, tom: Tom = 'ok'): void {
  const el = document.createElement('div');
  el.className = `aviso-flut ${tom === 'ok' ? 'af-ok' : 'af-atencao'}`;
  el.textContent = mensagem;
  area().appendChild(el);

  const duracao = Math.min(6000, Math.max(2500, mensagem.length * 60));
  setTimeout(() => {
    el.classList.add('saindo');
    // Espera a transição terminar antes de tirar do documento, senão ele
    // some de repente em vez de esmaecer.
    setTimeout(() => el.remove(), 300);
  }, duracao);

  // Clicar dispensa na hora — para quem já leu e quer a tela limpa.
  el.addEventListener('click', () => el.remove());
}

/**
 * Erro e atenção: continuam bloqueando.
 *
 * Existe como função própria, e não como `alert()` solto, para que o dia em
 * que quisermos trocar o visual do erro seja uma mudança em um lugar — e para
 * deixar explícito no código quando a interrupção é DELIBERADA.
 */
export function avisarBloqueante(mensagem: string): void {
  alert(mensagem);
}
